import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import firestoreService from '../../../services/firestoreService';
import useAuth from '../../../hooks/auth/useAuth';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';

const STEPS = [
  { id: 1, title: '내 답안', sub: '저장한 답안을 확인하고 고칩니다' },
  { id: 2, title: '표준답안 비교', sub: '빠진 키워드를 확인합니다' },
  { id: 3, title: '다시 쓰기', sub: '고친 답안을 평가받습니다' },
];

export default function LectureReview({ nav, onNavChangeHandler }) {
  const { id: lectureId } = useParams();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: 문제풀이, 2: 표준답안 비교, 3: 리라이팅
  const [userAnswer, setUserAnswer] = useState('');
  const [rewriteAnswer, setRewriteAnswer] = useState('');
  const [standardAnswer, setStandardAnswer] = useState('');
  const [highlightedText, setHighlightedText] = useState('');
  const [circledWords, setCircledWords] = useState([]);
  const [triangleWords, setTriangleWords] = useState([]);
  const [highlightedWords, setHighlightedWords] = useState([]);
  const [rewriteCount, setRewriteCount] = useState(0);
  const [isPerfect, setIsPerfect] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('리라이팅 데이터 로딩 중...');
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [isGptEvaluating, setIsGptEvaluating] = useState(false);
  const [useGptEvaluation, setUseGptEvaluation] = useState(true);
  
  const textareaRef = useRef(null);

  // 표준답안 데이터
  const [standardAnswerData, setStandardAnswerData] = useState({
    text: "",
    keywords: [],
    proof: []
  });
  
  const [rewritingProblem, setRewritingProblem] = useState({
    question: "리라이팅 문제",
    content: "이전에 풀었던 문제를 다시 처음부터 풀어보세요.",
    instruction: "핵심 키워드에는 형광펜을, 증명에서 핵심어(공주와 개주)에는 동그라미(공주)와 세모(개주) 표시를 해주세요."
  });

  // 데이터 로딩 함수들
  const fetchRewritingProblemData = async () => {
    try {
      setLoadingMessage('리라이팅 문제 로딩 중...');
      const result = await firestoreService.getDocument(`LectureRewritingProblem`, lectureId);
      if (result.success && result.data) {
        setRewritingProblem({
          question: result.data.question || "리라이팅 문제",
          content: result.data.content || "이전에 풀었던 문제를 다시 처음부터 풀어보세요.",
          instruction: result.data.instruction || "핵심 키워드에는 형광펜을, 증명에서 핵심어(공주와 개주)에는 동그라미(공주)와 세모(개주) 표시를 해주세요."
        });
      }
    } catch (error) {
      console.error('리라이팅 문제 로딩 실패:', error);
    }
  };

  const fetchStandardAnswerData = async () => {
    try {
      setLoadingMessage('표준답안 로딩 중...');
      const result = await firestoreService.getDocument(`LectureStandardAnswer`, lectureId);
      
      if (result.success && result.data) {
        const data = result.data;
        setStandardAnswerData({
          text: data.answer || "표준답안이 등록되지 않았습니다.",
          keywords: data.keywords ? data.keywords.split(',').map(k => k.trim()) : [],
          proof: data.proof_elements ? data.proof_elements.split(',').map(p => p.trim()) : []
        });
      } else {
        // 데이터가 없을 때 기본값 설정
        setStandardAnswerData({
          text: "표준답안이 등록되지 않았습니다.",
          keywords: [],
          proof: []
        });
      }
    } catch (error) {
      console.error('표준답안 로딩 실패:', error);
      // 오류 시 기본값 설정
      setStandardAnswerData({
        text: "표준답안을 불러올 수 없습니다.",
        keywords: [],
        proof: []
      });
    }
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchRewritingProblemData(),
          fetchStandardAnswerData(),
          (async () => {
            if (!user?.uid) return;
            const writing = await firestoreService.getDocument(
              `LectureProgress/${lectureId}/LectureWriting`,
              user.uid
            );
            if (writing.success && writing.data?.writing_content) {
              setUserAnswer((current) => current || writing.data.writing_content);
            }
          })(),
        ]);
      } catch (error) {
        console.error('데이터 로딩 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (lectureId) {
      loadData();
    }
  }, [lectureId, user?.uid]);

  const copyText = async (text) => {
    if (!text) {
      showToast('복사할 내용이 없습니다.', 'warn');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showToast('복사했습니다.');
    } catch (error) {
      showToast('복사하지 못했습니다.', 'warn');
    }
  };

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4200);
    return () => clearTimeout(t);
  }, [toast]);

  const handleTextSelection = (e) => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    
    if (selectedText) {
      setSelectedText(selectedText);
      setHighlightedText(selectedText);
      
      // 선택된 텍스트의 위치 정보 저장
      if (selection.rangeCount > 0) {
        setSelectionRange(selection.getRangeAt(0));
      }
    }
  };

  const handleKeywordMark = (type) => {
    if (!selectedText.trim()) {
      showToast('답안에서 표시할 문장을 드래그한 뒤, 아래 버튼을 눌러 주세요.', 'warn');
      return;
    }
    
    const newMark = {
      text: selectedText,
      type: type,
      id: Date.now(),
      position: selectionRange ? {
        start: selectionRange.startOffset,
        end: selectionRange.endOffset
      } : null
    };
    
    if (type === 'highlight') {
      setHighlightedWords(prev => [...prev, newMark]);
    } else if (type === 'circle') {
      setCircledWords(prev => [...prev, newMark]);
    } else if (type === 'triangle') {
      setTriangleWords(prev => [...prev, newMark]);
    }
    
    // 선택 해제
    setSelectedText('');
    setHighlightedText('');
    setSelectionRange(null);
    window.getSelection().removeAllRanges();
  };

  const removeMark = (id, type) => {
    if (type === 'highlight') {
      setHighlightedWords(prev => prev.filter(mark => mark.id !== id));
    } else if (type === 'circle') {
      setCircledWords(prev => prev.filter(mark => mark.id !== id));
    } else if (type === 'triangle') {
      setTriangleWords(prev => prev.filter(mark => mark.id !== id));
    }
  };

  const goRewrite = () => {
    setRewriteAnswer((current) => current || userAnswer);
    setCurrentStep(3);
  };

  const handleRewrite = () => {
    setRewriteCount((prev) => prev + 1);
    setRewriteAnswer('');
    setEvaluationResult(null);
    setIsPerfect(false);
  };

  const handleSubmit = async () => {
    if (!rewriteAnswer.trim()) {
      showToast('다시 쓴 답안을 먼저 작성해 주세요.', 'warn');
      return;
    }
    await handleGptEvaluation();
  };

  // Firebase Functions 초기화 (asia-northeast3 리전 지정)
  const functions = getFunctions(getApp(), 'asia-northeast3');
  const evaluateRewriting = httpsCallable(functions, 'evaluateRewriting');

  // Firebase Functions를 사용한 GPT 평가 함수
  const handleGptEvaluation = async () => {
    setIsGptEvaluating(true);
    setLoadingMessage('AI가 분석하고 있습니다...');
    
    try {
      // Firebase Functions 호출
      
      const result = await evaluateRewriting({
        userAnswer: rewriteAnswer,
        standardAnswer: standardAnswerData.text,
        keywords: standardAnswerData.keywords,
        proofElements: standardAnswerData.proof,
        userId: user?.uid || 'anonymous',
        lectureId
      });
      
      if (result.data.success) {
        const evaluation = result.data.evaluation;
        
        // JSON 파싱된 결과인지 확인
        if (evaluation.score !== undefined) {
          const gptEvaluation = {
            isPerfect: evaluation.isPerfect,
            score: evaluation.score,
            totalScore: 100,
            feedback: [
              evaluation.feedback?.keywords,
              evaluation.feedback?.proof,
              evaluation.feedback?.structure,
              evaluation.feedback?.overall
            ].filter(f => f), // 빈 문자열 제거
            missingKeywords: evaluation.missingElements?.keywords || [],
            missingProof: evaluation.missingElements?.proof || [],
            suggestions: evaluation.suggestions || []
          };
          
          setEvaluationResult(gptEvaluation);
          
          if (gptEvaluation.isPerfect) {
            setIsPerfect(true);
          }
        } else {
          // 파싱 실패 시 로컬 평가로 fallback
          console.warn('GPT 응답 파싱 실패, 로컬 평가로 전환');
          const evaluation = evaluateAnswer(rewriteAnswer, standardAnswerData);
          setEvaluationResult(evaluation);
          
          if (evaluation.isPerfect) {
            setIsPerfect(true);
          }
        }
      } else {
        // GPT 평가 실패 시 로컬 평가로 fallback
        console.warn('GPT 평가 실패, 로컬 평가로 전환:', result.data.error);
        const evaluation = evaluateAnswer(rewriteAnswer, standardAnswerData);
        setEvaluationResult(evaluation);
        
        if (evaluation.isPerfect) {
          setIsPerfect(true);
        }
      }
    } catch (error) {
      console.error('GPT 평가 오류:', error);
      // 오류 시 로컬 평가로 fallback
      const evaluation = evaluateAnswer(rewriteAnswer, standardAnswerData);
      setEvaluationResult(evaluation);
      
      if (evaluation.isPerfect) {
        setIsPerfect(true);
      }
    } finally {
      setIsGptEvaluating(false);
    }
  };

  // 답안 평가 함수
  const evaluateAnswer = (answer, standard) => {
    const evaluation = {
      isPerfect: false,
      score: 0,
      totalScore: 100,
      missingKeywords: [],
      missingProof: [],
      feedback: []
    };

    // 1. 핵심 키워드 체크 (40점)
    const foundKeywords = standard.keywords.filter(keyword =>
      answer.toLowerCase().includes(keyword.toLowerCase())
    );
    if (standard.keywords.length > 0) {
      const keywordScore = (foundKeywords.length / standard.keywords.length) * 40;
      evaluation.score += keywordScore;
      if (foundKeywords.length < standard.keywords.length) {
        evaluation.missingKeywords = standard.keywords.filter(
          (keyword) => !foundKeywords.includes(keyword)
        );
        evaluation.feedback.push(`핵심 키워드가 부족합니다: ${evaluation.missingKeywords.join(', ')}`);
      }
    } else {
      evaluation.score += 40;
    }

    // 2. 증명 요소 체크 (30점)
    const foundProof = standard.proof.filter((proof) =>
      answer.toLowerCase().includes(proof.toLowerCase())
    );
    if (standard.proof.length > 0) {
      const proofScore = (foundProof.length / standard.proof.length) * 30;
      evaluation.score += proofScore;
      if (foundProof.length < standard.proof.length) {
        evaluation.missingProof = standard.proof.filter((proof) => !foundProof.includes(proof));
        evaluation.feedback.push(`증명 요소가 부족합니다: ${evaluation.missingProof.join(', ')}`);
      }
    } else {
      evaluation.score += 30;
    }

    // 3. 답안 길이 체크 (10점)
    const minLength = 100; // 최소 100자
    if (answer.length >= minLength) {
      evaluation.score += 10;
    } else {
      evaluation.feedback.push(`답안이 너무 짧습니다. 최소 ${minLength}자 이상 작성해주세요.`);
    }

    // 4. 문장 구조 체크 (20점)
    const sentenceCount = (answer.match(/[.!?]/g) || []).length;
    if (sentenceCount >= 3) {
      evaluation.score += 20;
    } else {
      evaluation.feedback.push('더 많은 문장으로 구성해주세요.');
    }

    // 완벽도 판정 (90점 이상)
    evaluation.isPerfect = evaluation.score >= 90;
    
    return evaluation;
  };

  // 표준답안 텍스트에서 키워드들을 하이라이트하는 함수
  const highlightKeywordsInText = (text) => {
    if (!text) return text;
    
    let highlightedText = text;
    
    // 핵심 키워드들을 형광펜으로 표시
    standardAnswerData.keywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      highlightedText = highlightedText.replace(regex, `<span class="highlight-keyword">$1</span>`);
    });
    
    // 증명 요소들을 표시 (공주는 동그라미, 개주는 세모)
    standardAnswerData.proof.forEach(proof => {
      const regex = new RegExp(`(${proof})`, 'gi');
      if (proof === '공주') {
        highlightedText = highlightedText.replace(regex, `<span class="circle-keyword">$1</span>`);
      } else if (proof === '개주') {
        highlightedText = highlightedText.replace(regex, `<span class="triangle-keyword">$1</span>`);
      }
    });
    
    return highlightedText;
  };

  const renderStep1 = () => (
    <InfoCard>
      <CardHeader $compact>
        <CardTitleRow>
          <CardTitle>1단계 · 내 답안</CardTitle>
          <StepBadge>현재</StepBadge>
        </CardTitleRow>
        <CardSubtitle>AI 첨삭에서 저장한 답안을 불러옵니다. 고친 뒤 표준답안과 비교하세요.</CardSubtitle>
      </CardHeader>
      <CardContent $pad>
        <ProblemBox>
          <ProblemTitle>{rewritingProblem.question}</ProblemTitle>
          <ProblemText>{rewritingProblem.content}</ProblemText>
          <InstructionText>{rewritingProblem.instruction}</InstructionText>
        </ProblemBox>

        <AnswerSection>
          <AnswerToolbar>
            <AnswerLabel htmlFor="review-answer-step1">답안 작성</AnswerLabel>
            <CharMeta>{userAnswer.length}자</CharMeta>
          </AnswerToolbar>
          <TextEditorContainer>
            <AnswerTextarea
              id="review-answer-step1"
              ref={textareaRef}
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onMouseUp={handleTextSelection}
              onSelect={handleTextSelection}
              placeholder="여기에 답안을 쓰거나, 불러온 답안을 고치세요."
            />
            <MarkOverlay>
              {highlightedWords.map((mark) => (
                <MarkSpan key={mark.id} type="highlight">
                  {mark.text}
                  <RemoveButton type="button" onClick={() => removeMark(mark.id, 'highlight')} aria-label="표시 제거">
                    ×
                  </RemoveButton>
                </MarkSpan>
              ))}
              {circledWords.map((mark) => (
                <MarkSpan key={mark.id} type="circle">
                  {mark.text}
                  <RemoveButton type="button" onClick={() => removeMark(mark.id, 'circle')} aria-label="표시 제거">
                    ×
                  </RemoveButton>
                </MarkSpan>
              ))}
              {triangleWords.map((mark) => (
                <MarkSpan key={mark.id} type="triangle">
                  {mark.text}
                  <RemoveButton type="button" onClick={() => removeMark(mark.id, 'triangle')} aria-label="표시 제거">
                    ×
                  </RemoveButton>
                </MarkSpan>
              ))}
            </MarkOverlay>
          </TextEditorContainer>

          {highlightedText && <SelectionChip>선택됨: “{highlightedText}”</SelectionChip>}

          <UsageDetails>
            <UsageSummary>표시 연습은 선택입니다</UsageSummary>
            <UsageBody>
          <MarkingToolsScroll role="toolbar" aria-label="답안 표시 도구">
            <MarkButton type="button" onClick={() => handleKeywordMark('highlight')}>
              형광펜
              <MarkButtonHint>키워드</MarkButtonHint>
            </MarkButton>
            <MarkButton type="button" onClick={() => handleKeywordMark('circle')}>
              동그라미
              <MarkButtonHint>공통 주제</MarkButtonHint>
            </MarkButton>
            <MarkButton type="button" onClick={() => handleKeywordMark('triangle')}>
              세모
              <MarkButtonHint>개별 주제</MarkButtonHint>
            </MarkButton>
          </MarkingToolsScroll>
              <GuideList>
                <li>문장을 드래그한 뒤 형광펜, 동그라미, 세모를 누릅니다.</li>
                <li>비교와 다시 쓰기는 이 표시 없이도 진행할 수 있습니다.</li>
              </GuideList>
            </UsageBody>
          </UsageDetails>

          {(highlightedWords.length > 0 || circledWords.length > 0 || triangleWords.length > 0) && (
            <MarksDetails>
              <MarksSummary>적용한 표시 {highlightedWords.length + circledWords.length + triangleWords.length}건</MarksSummary>
              <MarksPanel>
                {highlightedWords.map((mark) => (
                  <MarkItem key={mark.id} type="highlight">
                    형광 {mark.text}
                    <RemoveButton type="button" onClick={() => removeMark(mark.id, 'highlight')} aria-label="삭제">
                      ×
                    </RemoveButton>
                  </MarkItem>
                ))}
                {circledWords.map((mark) => (
                  <MarkItem key={mark.id} type="circle">
                    동그라미 {mark.text}
                    <RemoveButton type="button" onClick={() => removeMark(mark.id, 'circle')} aria-label="삭제">
                      ×
                    </RemoveButton>
                  </MarkItem>
                ))}
                {triangleWords.map((mark) => (
                  <MarkItem key={mark.id} type="triangle">
                    세모 {mark.text}
                    <RemoveButton type="button" onClick={() => removeMark(mark.id, 'triangle')} aria-label="삭제">
                      ×
                    </RemoveButton>
                  </MarkItem>
                ))}
              </MarksPanel>
            </MarksDetails>
          )}
        </AnswerSection>

        <ButtonGroup $spread>
          <NextButton type="button" onClick={() => setCurrentStep(2)} disabled={!userAnswer.trim()}>
            다음 · 표준답안 비교
          </NextButton>
        </ButtonGroup>
      </CardContent>
    </InfoCard>
  );

  const renderStep2 = () => (
    <InfoCard>
      <CardHeader $compact>
        <CardTitleRow>
          <CardTitle>2단계 · 표준답안 비교</CardTitle>
          <StepBadge>현재</StepBadge>
        </CardTitleRow>
        <CardSubtitle>1단계에서 쓴 답안과 모범 답안을 나란히 보며 차이를 점검합니다.</CardSubtitle>
      </CardHeader>
      <CardContent $pad>
        <CheckList>
          {standardAnswerData.keywords.filter(Boolean).map((keyword) => {
            const hit = userAnswer.toLowerCase().includes(keyword.toLowerCase());
            return (
              <CheckItem key={keyword} $hit={hit}>
                {hit ? '포함' : '빠짐'} · {keyword}
              </CheckItem>
            );
          })}
          {standardAnswerData.proof.filter(Boolean).map((proof) => {
            const hit = userAnswer.toLowerCase().includes(proof.toLowerCase());
            return (
              <CheckItem key={proof} $hit={hit}>
                {hit ? '포함' : '빠짐'} · {proof}
              </CheckItem>
            );
          })}
        </CheckList>
        <ComparisonSection>
          <UserAnswerBox>
            <BoxTitle>내 답안</BoxTitle>
            <UserAnswerText>
              {userAnswer || '작성된 답안이 없습니다.'}
              {highlightedWords.map((mark) => (
                <MarkSpan key={mark.id} type="highlight">
                  {mark.text}
                </MarkSpan>
              ))}
              {circledWords.map((mark) => (
                <MarkSpan key={mark.id} type="circle">
                  {mark.text}
                </MarkSpan>
              ))}
              {triangleWords.map((mark) => (
                <MarkSpan key={mark.id} type="triangle">
                  {mark.text}
                </MarkSpan>
              ))}
            </UserAnswerText>
          </UserAnswerBox>

          <StandardAnswerBox>
            <BoxTitle>
              표준답안
              <CopyText type="button" onClick={() => copyText(standardAnswerData.text)}>복사</CopyText>
            </BoxTitle>
            <StandardAnswerText
              dangerouslySetInnerHTML={{
                __html: highlightKeywordsInText(standardAnswerData.text),
              }}
            />
            <KeywordsList>
              <KeywordTitle>핵심 키워드</KeywordTitle>
              {standardAnswerData.keywords.map((keyword, index) => (
                <KeywordTag key={index}>{keyword}</KeywordTag>
              ))}
            </KeywordsList>
            <KeywordsList>
              <KeywordTitle>증명 요소</KeywordTitle>
              {standardAnswerData.proof.map((proof, index) => (
                <KeywordTag key={index} type="proof">
                  {proof}
                </KeywordTag>
              ))}
            </KeywordsList>
          </StandardAnswerBox>
        </ComparisonSection>

        <ReflectionDetails>
          <ReflectionSummary>스스로 점검하기</ReflectionSummary>
          <ReflectionBody>
            <ReflectionList>
              <li>표준답안에 있는 핵심 키워드를 내 답안에 모두 썼나요?</li>
              <li>문장·논리 전개에서 아쉬운 부분은 없나요?</li>
              <li>구조(서론·본론·결론) 차이는 없나요?</li>
            </ReflectionList>
          </ReflectionBody>
        </ReflectionDetails>

        <ButtonGroup $spread>
          <BackButton type="button" onClick={() => setCurrentStep(1)}>
            이전
          </BackButton>
          <NextButton type="button" onClick={goRewrite}>
            이 답을 고쳐서 다시 쓰기
          </NextButton>
        </ButtonGroup>
      </CardContent>
    </InfoCard>
  );

  const renderStep3 = () => (
    <InfoCard>
      <CardHeader $compact>
        <CardTitleRow>
          <CardTitle>3단계 · 다시 쓰기</CardTitle>
          <StepBadge>현재</StepBadge>
        </CardTitleRow>
        <CardSubtitle>비교한 답안을 바탕으로 고칩니다. 평가는 이 답안만 봅니다.</CardSubtitle>
      </CardHeader>
      <CardContent $pad>
        <RewriteInfo>
          <RewriteStat>
            리라이팅 <strong>{rewriteCount}</strong>회
          </RewriteStat>
          {isPerfect && (
            <PerfectBadge>
              <CheckCircleOutline style={{ fontSize: 18, marginRight: 4 }} aria-hidden />
              완벽 달성
            </PerfectBadge>
          )}
        </RewriteInfo>

        <AnswerSection>
          <AnswerToolbar>
            <AnswerLabel htmlFor="review-answer-step3">리라이팅 답안</AnswerLabel>
            <CharMeta>{rewriteAnswer.length}자</CharMeta>
          </AnswerToolbar>
          <AnswerTextarea
            id="review-answer-step3"
            value={rewriteAnswer}
            onChange={(e) => setRewriteAnswer(e.target.value)}
            placeholder="비교에서 빠진 내용을 넣어 다시 써 보세요."
          />
        </AnswerSection>

        {isGptEvaluating && (
          <GptLoadingBar role="status">
            <MiniSpinner aria-hidden />
            <span>{loadingMessage || '평가 중입니다…'}</span>
          </GptLoadingBar>
        )}

        <ButtonGroup $spread>
          <RewriteButton type="button" onClick={handleRewrite}>
            비우고 새로 쓰기
          </RewriteButton>
          <SubmitButton type="button" onClick={handleSubmit} disabled={isGptEvaluating || !rewriteAnswer.trim()}>
            {isGptEvaluating ? '평가 중…' : '완벽도 체크'}
          </SubmitButton>
        </ButtonGroup>

        {evaluationResult && (
          <EvaluationResult>
            <EvaluationTitle>평가 결과</EvaluationTitle>
            <ScoreDisplay>
              <ScoreNumber>{Math.round(evaluationResult.score)}</ScoreNumber>
              <ScoreTotal>/ 100점</ScoreTotal>
              <ScoreBar>
                <ScoreFill width={evaluationResult.score} />
              </ScoreBar>
            </ScoreDisplay>

            {evaluationResult.feedback.length > 0 && (
              <FeedbackSection>
                <FeedbackTitle>개선 포인트</FeedbackTitle>
                {evaluationResult.feedback.map((feedback, index) => (
                  <FeedbackItem key={index}>{feedback}</FeedbackItem>
                ))}
              </FeedbackSection>
            )}

            {evaluationResult.suggestions && evaluationResult.suggestions.length > 0 && (
              <SuggestionsSection>
                <SuggestionsTitle>AI 제안</SuggestionsTitle>
                {evaluationResult.suggestions.map((suggestion, index) => (
                  <SuggestionItem key={index}>{suggestion}</SuggestionItem>
                ))}
              </SuggestionsSection>
            )}

            <EvaluationMessage>
              {evaluationResult.score < 50
                ? '아직 많이 부족합니다. 다시 시도해 보세요.'
                : evaluationResult.score < 80
                  ? '좋은 시작입니다. 조금만 더 다듬어 보세요.'
                  : evaluationResult.score < 90
                    ? '거의 완벽합니다. 조금만 더 보완하면 됩니다.'
                    : '훌륭합니다. 이 수준을 유지해 보세요.'}
            </EvaluationMessage>
          </EvaluationResult>
        )}

        {isPerfect && (
          <SuccessMessage>
            축하합니다. 목표 점수에 도달했습니다.
            <br />
            수업 시간에 제출할 답안으로 활용해 보세요.
          </SuccessMessage>
        )}
      </CardContent>
    </InfoCard>
  );

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <Container>
        <LoadingCard>
          <LoadingSpinner />
          <LoadingText>{loadingMessage}</LoadingText>
        </LoadingCard>
      </Container>
    );
  }

  // 데이터가 없을 때 표시
  return (
    <Container>
      {toast && (
        <ToastBar $warn={toast.type === 'warn'} role="alert">
          <ToastText>{toast.message}</ToastText>
          <ToastClose type="button" onClick={() => setToast(null)} aria-label="닫기">
            <CloseRounded style={{ fontSize: 20 }} />
          </ToastClose>
        </ToastBar>
      )}

      <IntroBanner>
        <IntroIcon aria-hidden>
          <MenuBookOutlined style={{ fontSize: 24 }} />
        </IntroIcon>
        <IntroCopy>
          <IntroTitle>리라이팅</IntroTitle>
          <IntroDesc>
            저장한 답안을 표준답안과 비교한 뒤, 빠진 내용을 넣어 다시 씁니다.
          </IntroDesc>
        </IntroCopy>
      </IntroBanner>

      <Stepper aria-label="복습 단계">
        {STEPS.map((s, idx) => {
          const done = currentStep > s.id;
          const active = currentStep === s.id;
          return (
            <StepperItem key={s.id}>
              {idx > 0 && <StepperLine $done={currentStep > STEPS[idx - 1].id} aria-hidden />}
              <StepperNode>
                <StepCircle $active={active} $done={done}>
                  {done ? <CheckCircleOutline style={{ fontSize: 22 }} /> : s.id}
                </StepCircle>
                <StepLabel $active={active}>{s.title}</StepLabel>
                <StepperSub>{s.sub}</StepperSub>
              </StepperNode>
            </StepperItem>
          );
        })}
      </Stepper>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding-bottom: 32px;
  position: relative;
`;

const ToastBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$warn ? '#fecaca' : '#e7e1d3')};
  background: ${(p) => (p.$warn ? '#fef2f2' : '#fffbea')};
  color: ${(p) => (p.$warn ? '#991b1b' : '#1d2340')};
  font-size: 14px;
  line-height: 1.45;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
`;

const ToastText = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 600;
`;

const ToastClose = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  &:hover {
    opacity: 1;
  }
`;

const IntroBanner = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 4px;
  background: #fffbea;
  border: 1px solid #efe4bd;
`;

const IntroIcon = styled.div`
  color: #2c3561;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IntroCopy = styled.div`
  min-width: 0;
`;

const IntroTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: #1d2340;
  letter-spacing: -0.02em;
`;

const IntroDesc = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #1d2340;
  strong {
    font-weight: 700;
    color: #1d2340;
  }
`;

const Stepper = styled.ol`
  list-style: none;
  margin: 0;
  padding: 16px 14px;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 0;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const StepperItem = styled.li`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  position: relative;
`;

const StepperLine = styled.div`
  flex: 0 0 12px;
  height: 3px;
  margin: 0 4px;
  align-self: center;
  margin-top: -36px;
  border-radius: 2px;
  background: ${(p) => (p.$done ? '#1d2340' : '#e2e8f0')};
  @media (max-width: 600px) {
    display: none;
  }
`;

const StepperNode = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 4px 6px;
  min-width: 0;
`;

const StepCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  flex-shrink: 0;
  color: ${(p) => {
    if (p.$active) return '#fff';
    if (p.$done) return '#fff';
    return '#64748b';
  }};
  background: ${(p) => {
    if (p.$active) return '#1d2340';
    if (p.$done) return '#22c55e';
    return '#f1f5f9';
  }};
  border: ${(p) => (!p.$active && !p.$done ? '1px solid #e2e8f0' : 'none')};
  box-shadow: ${(p) => (p.$active ? '0 4px 12px rgba(79, 70, 229, 0.35)' : 'none')};
`;

const StepLabel = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${(p) => (p.$active ? '#0f172a' : '#64748b')};
  line-height: 1.25;
`;

const StepperSub = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  line-height: 1.35;
  max-width: 140px;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(29, 35, 64, 0.03), rgba(29, 35, 64, 0.03));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${(p) => (p.$compact ? '8px' : '12px')};
  padding: ${(p) => (p.$compact ? '18px 22px' : '24px 32px')};
  background: #1d2340;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
    opacity: 0.3;
  }
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 1;
  letter-spacing: -0.02em;
`;

const StepBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
`;

const CardSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.92);
  position: relative;
  z-index: 1;
  font-weight: 500;
`;

const CardContent = styled.div`
  padding: ${(p) => (p.$pad ? '20px 22px 24px' : '32px')};
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: ${(p) => (p.$pad ? '16px 16px 20px' : '24px 16px')};
  }
`;

const ProblemBox = styled.div`
  background: #fffdf7;
  border: 1px solid #e7e1d3;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
`;

const ProblemTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '📝';
    font-size: 1.2rem;
  }
`;

const ProblemText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #4a5568;
  margin: 0 0 16px 0;
  position: relative;
  white-space: pre-wrap;
  z-index: 1;
`;

const InstructionText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #1d2340;
  background: #fffbea;
  border: 1px solid #efe4bd;
  border-radius: 4px;
  padding: 12px;
  font-weight: 500;
`;

const AnswerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
`;

const AnswerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CharMeta = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  padding: 4px 10px;
  background: #f1f5f9;
  border-radius: 999px;
`;

const AnswerLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
`;

const TextEditorContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 220px;
`;

const AnswerTextarea = styled.textarea`
  width: 100%;
  min-height: 260px;
  padding: 20px;
  border: 2px solid rgba(255, 229, 138, 0.55);
  border-radius: 14px;
  font-size: 15px;
  line-height: 1.75;
  resize: vertical;
  font-family: inherit;
  background: #fafafa;
  color: #0f172a;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1d2340;
    box-shadow: 0 0 0 3px rgba(255, 229, 138, 0.55);
    background: #fff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const MarkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
  padding: 20px;
  font-size: 15px;
  line-height: 1.75;
  color: transparent;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const MarkSpan = styled.span`
  position: relative;
  display: inline;
  background: ${props => {
    switch(props.type) {
      case 'highlight': return 'linear-gradient(120deg, #ffeb3b 0%, #ffc107 100%)';
      case 'circle': return 'transparent';
      case 'triangle': return 'transparent';
      default: return 'transparent';
    }
  }};
  color: ${props => props.type === 'highlight' ? '#333' : 'inherit'};
  padding: ${props => props.type === 'highlight' ? '2px 4px' : '0'};
  border-radius: ${props => props.type === 'highlight' ? '4px' : '0'};
  font-weight: 600;
  margin: 0 1px;
  box-shadow: ${props => props.type === 'highlight' ? '0 1px 3px rgba(0, 0, 0, 0.2)' : 'none'};
  
  /* 동그라미 표시 */
  ${props => props.type === 'circle' && `
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 8px);
      height: calc(100% + 8px);
      border: 3px solid #1d2340;
      border-radius: 50%;
      z-index: -1;
    }
  `}
  
  /* 세모 표시 */
  ${props => props.type === 'triangle' && `
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 12px solid #ff9800;
      z-index: -1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 10px solid #fff;
      z-index: 0;
    }
  `}
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ff4444;
  color: white;
  border: none;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  pointer-events: auto;
  
  &:hover {
    background: #cc0000;
    transform: scale(1.1);
  }
`;

const SelectionChip = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #854d0e;
  background: #fef9c3;
  border: 1px solid #fde047;
  line-height: 1.4;
`;

const MarkingToolsScroll = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding: 4px 2px 8px;
  margin: 0 -2px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const MarkButtonHint = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 600;
  opacity: 0.85;
  margin-top: 2px;
`;

const MarkButton = styled.button`
  flex: 0 0 auto;
  scroll-snap-align: start;
  min-width: 108px;
  padding: 12px 14px;
  border: 2px solid #1d2340;
  background: white;
  color: #1d2340;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;

  &:hover {
    background: #1d2340;
    color: white;
    box-shadow: 0 6px 18px rgba(79, 70, 229, 0.35);
    transform: translateY(-1px);
  }
`;

const UsageDetails = styled.details`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const UsageSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  &::-webkit-details-marker {
    display: none;
  }
`;

const UsageBody = styled.div`
  padding: 0 14px 14px;
`;

const GuideList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
  li + li {
    margin-top: 6px;
  }
`;

const MarksDetails = styled.details`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
`;

const MarksSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  user-select: none;
  &::-webkit-details-marker {
    display: none;
  }
`;

const MarksPanel = styled.div`
  padding: 0 12px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MarkItem = styled.div`
  display: inline-block;
  margin: 4px 8px 4px 0;
  padding: 6px 12px;
  background: ${props => {
    switch(props.type) {
      case 'highlight': return 'linear-gradient(135deg, #ffeb3b 0%, #ffc107 100%)';
      case 'circle': return 'transparent';
      case 'triangle': return 'transparent';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => props.type === 'highlight' ? '#333' : 'inherit'};
  border-radius: ${props => props.type === 'highlight' ? '16px' : '0'};
  font-size: 12px;
  font-weight: 600;
  position: relative;
  box-shadow: ${props => props.type === 'highlight' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  
  /* 동그라미 표시 */
  ${props => props.type === 'circle' && `
    border: 2px solid #1d2340;
    border-radius: 50%;
    padding: 4px 8px;
  `}
  
  /* 세모 표시 */
  ${props => props.type === 'triangle' && `
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 12px solid #ff9800;
      z-index: -1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 10px solid #fff;
      z-index: 0;
    }
  `}
  
  button {
    margin-left: 8px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.3);
    color: white;
    border: none;
    font-size: 10px;
    font-weight: bold;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(0, 0, 0, 0.5);
    }
`;

const CheckList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const CheckItem = styled.span`
  padding: 6px 10px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $hit }) => ($hit ? '#1d2340' : '#9d2d1d')};
  background: ${({ $hit }) => ($hit ? '#ffe58a' : '#fff4f1')};
  border: 1px solid ${({ $hit }) => ($hit ? '#ffe58a' : '#f0c8bf')};
`;

const ComparisonSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @container lectureDetailContent (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserAnswerBox = styled.div`
  background: #fff;
  border: 1px solid #e7e1d3;
  border-left: 4px solid #1d2340;
  border-radius: 4px;
  padding: 20px;
`;

const StandardAnswerBox = styled.div`
  background: #fffdf7;
  border: 1px solid #e7e1d3;
  border-left: 4px solid #d9412b;
  border-radius: 4px;
  padding: 20px;
`;

const BoxTitle = styled.h4`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 16px 0;
  color: #1d2340;
`;

const CopyText = styled.button`
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
  color: #1d2340;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 4px;
  cursor: pointer;
`;

const AnswerText = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #4a5568;
  white-space: pre-wrap;
  position: relative;
  z-index: 1;
`;

const UserAnswerText = styled.div`
  font-size: 15px;
  line-height: 1.75;
  color: #334155;
  white-space: pre-wrap;
  position: relative;
  z-index: 1;
  min-height: 120px;
  max-height: min(42vh, 340px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StandardAnswerText = styled.div`
  font-size: 15px;
  line-height: 1.75;
  color: #334155;
  white-space: pre-wrap;
  position: relative;
  z-index: 1;
  max-height: min(42vh, 340px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  /* 표준답안 내 키워드 하이라이트 */
  .highlight-keyword {
    background: linear-gradient(120deg, #ffeb3b 0%, #ffc107 100%);
    color: #333;
    padding: 2px 4px;
    border-radius: 4px;
    font-weight: 600;
    margin: 0 1px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  .circle-keyword {
    position: relative;
    display: inline;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 8px);
      height: calc(100% + 8px);
      border: 3px solid #1d2340;
      border-radius: 50%;
      z-index: -1;
    }
  }
  
  .triangle-keyword {
    position: relative;
    display: inline;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 12px solid #ff9800;
      z-index: -1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 10px solid #fff;
      z-index: 0;
    }
  }
`;

const KeywordsList = styled.div`
  margin-top: 16px;
  position: relative;
  z-index: 1;
`;

const KeywordTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 8px 0;
`;

const KeywordTag = styled.span`
  display: inline-block;
  background: ${props => props.type === 'proof' 
    ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' 
    : '#1d2340'
  };
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  margin: 4px 4px 0 0;
  box-shadow: ${props => props.type === 'proof' 
    ? '0 2px 8px rgba(255, 152, 0, 0.2)' 
    : '0 2px 8px rgba(29, 35, 64, 0.16)'
  };
`;

const ReflectionDetails = styled.details`
  border: 1px solid #fde68a;
  border-radius: 14px;
  background: #fffbeb;
  margin-bottom: 20px;
`;

const ReflectionSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 800;
  color: #92400e;
  user-select: none;
  &::-webkit-details-marker {
    display: none;
  }
`;

const ReflectionBody = styled.div`
  padding: 0 16px 16px;
`;

const ReflectionList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.65;
  color: #78350f;
  li + li {
    margin-top: 8px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: ${(p) => (p.$spread ? 'space-between' : 'center')};
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  padding: 12px 24px;
  border: 2px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: #6c757d;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  }
`;

const NextButton = styled.button`
  padding: 14px 22px;
  border: none;
  background: #1d2340;
  color: white;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(79, 70, 229, 0.4);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const RewriteButton = styled.button`
  padding: 14px 20px;
  border: 2px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
`;

const SubmitButton = styled.button`
  padding: 14px 22px;
  border: none;
  background: #1d2340;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #2c3561;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const RewriteInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 20px;
`;

const RewriteStat = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  strong {
    color: #2c3561;
    font-weight: 800;
  }
`;

const GptLoadingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fffbea;
  border: 1px solid #e7e1d3;
  color: #3730a3;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const MiniSpinner = styled.span`
  width: 20px;
  height: 20px;
  border: 2px solid #e7e1d3;
  border-top-color: #2c3561;
  border-radius: 50%;
  animation: reviewSpin 0.75s linear infinite;
  @keyframes reviewSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PerfectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 2px 10px rgba(34, 197, 94, 0.35);
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  border: 2px solid rgba(40, 167, 69, 0.3);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #155724;
  margin-top: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #28a745 0%, #20c997 100%);
  }
`;

const LoadingCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 60px 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(29, 35, 64, 0.03), rgba(29, 35, 64, 0.03));
    opacity: 1;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(29, 35, 64, 0.08);
  border-top: 4px solid #1d2340;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #4a5568;
  font-weight: 500;
  position: relative;
  z-index: 1;
`;

const EmptyCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 60px 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(29, 35, 64, 0.03), rgba(29, 35, 64, 0.03));
    opacity: 1;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 16px 0;
  position: relative;
  z-index: 1;
`;

const EmptyText = styled.div`
  font-size: 16px;
  color: #4a5568;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

const EvaluationResult = styled.div`
  background: linear-gradient(135deg, rgba(29, 35, 64, 0.05) 0%, rgba(29, 35, 64, 0.05) 100%);
  border: 1px solid rgba(29, 35, 64, 0.16);
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: #1d2340;
  }
`;

const EvaluationTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px 0;
  position: relative;
  z-index: 1;
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const ScoreNumber = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: #1d2340;
`;

const ScoreTotal = styled.span`
  font-size: 18px;
  color: #4a5568;
  font-weight: 500;
`;

const ScoreBar = styled.div`
  flex: 1;
  height: 8px;
  background: rgba(29, 35, 64, 0.08);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ScoreFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #1d2340;
  border-radius: 4px;
  transition: width 0.6s ease;
`;

const FeedbackSection = styled.div`
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const FeedbackTitle = styled.h5`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 12px 0;
`;

const FeedbackItem = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #856404;
  line-height: 1.5;
`;

const EvaluationMessage = styled.div`
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #155724;
  position: relative;
  z-index: 1;
`;

const EvaluationOptions = styled.div`
  margin: 20px 0;
  padding: 16px;
  background: linear-gradient(135deg, rgba(29, 35, 64, 0.05) 0%, rgba(29, 35, 64, 0.05) 100%);
  border: 1px solid rgba(29, 35, 64, 0.08);
  border-radius: 12px;
`;

const EvaluationToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #1d2340;
    cursor: pointer;
  }
  
  label {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const SuggestionsSection = styled.div`
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
`;

const SuggestionsTitle = styled.h5`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 12px 0;
`;

const SuggestionItem = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #1976d2;
  line-height: 1.5;
  position: relative;
  
  &::before {
    content: '💡';
    margin-right: 8px;
  }
`;
